<template>
    <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ loadingAnnouncement }}</span>
    <div v-if="isFetching && !retrying" class="body-small flex items-center justify-center gap-2 p-10 text-ink-muted">
        <LoadingSpinner class="size-5" /> {{ t("developmentJourneys.goalProgress.loading") }}
    </div>
    <!-- The pressed button stays mounted while the retry runs: removing a focused control drops
         VoiceOver to the page and the loading announcement with it. -->
    <div v-else-if="error || !data || retrying" :role="retrying ? undefined : 'alert'" class="space-y-3 p-5">
        <p class="body-small text-ink-muted">{{ retrying ? t("developmentJourneys.goalProgress.loading") : t("developmentJourneys.goalProgress.loadError") }}</p>
        <BaseButton theme="neutral" size="sm" :disabled="retrying" @click="retry">{{ t("common.tryAgain") }}</BaseButton>
    </div>
    <PersonalOverview v-else :data="data">
        <template v-if="reflectionUrl && chatUrl">
            <ProgressReflection v-if="reflection?.message" :message="reflection.message" :busy="isStarting" @reflect="startReflection()" @actions="showActions()" />
            <div v-if="reflectionError" role="alert" class="body-small space-y-2 text-ink-muted">
                <p>{{ t("developmentJourneys.goalProgress.reflectionLoadError") }}</p>
                <BaseButton theme="tertiary" size="sm" @click="loadReflection()">{{ t("common.tryAgain") }}</BaseButton>
            </div>
            <p v-if="startError || navigationError" role="alert" class="body-small text-ink-muted">{{ t("developmentJourneys.goalProgress.reflectionStartError") }}</p>
        </template>
    </PersonalOverview>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { GoalProgressResponse } from "~vue/api/generated/development-journeys";
import type { ProgressEntranceResponse, ProgressReflectionResponse } from "~vue/api/generated/goal-progress-chat";
import BaseButton from "~vue/components/BaseButton.vue";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import PersonalOverview from "~vue/pages/developmentJourneys/progressInsights/PersonalOverview.vue";
import ProgressReflection from "~vue/pages/developmentJourneys/progressInsights/ProgressReflection.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const { reflectionUrl, chatUrl } = defineProps<{ reflectionUrl?: string | null; chatUrl?: string | null }>();
const emit = defineEmits<{ actions: [] }>();
const {
    data: reflection,
    error: reflectionError,
    execute: loadReflection,
} = useFetch(reflectionUrl || "", { immediate: !!reflectionUrl })
    .get()
    .json<ProgressReflectionResponse>();
const {
    data: entrance,
    error: startError,
    execute: openConversation,
} = useFetch(chatUrl || "", { immediate: false })
    .post({})
    .json<ProgressEntranceResponse>();
const isStarting = ref(false);
const navigationError = ref(false);
function showActions() {
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GOAL_PROGRESS_ACTIONS_CLICKED);
    emit("actions");
}
async function startReflection() {
    if (!chatUrl || isStarting.value || !reflection.value) {
        return;
    }
    void logUserInteraction(AmplitudeEventNames.DEVELOPMENT_JOURNEYS_GOAL_PROGRESS_REFLECTION_CLICKED, {
        goal_progress_reflection_case: reflection.value.case,
    });
    isStarting.value = true;
    navigationError.value = false;
    try {
        if (!entrance.value?.chat_url) {
            await openConversation();
        }
        if (startError.value || !entrance.value?.chat_url) {
            isStarting.value = false;
            return;
        }
        let navigated = false;
        router.visit(entrance.value.chat_url, {
            onSuccess: () => (navigated = true),
            onFinish: () => {
                isStarting.value = false;
                navigationError.value = !navigated;
            },
        });
    } catch {
        navigationError.value = true;
        isStarting.value = false;
    }
}
const { data, error, isFetching, execute } = useFetch("/v1/development-journeys/progress").get().json<GoalProgressResponse>();
const retrying = ref(false);
const loadingAnnouncement = useLoadingAnnouncement(isFetching, () =>
    retrying.value ? t("developmentJourneys.goalProgress.tryingAgain") : t("developmentJourneys.goalProgress.loading"),
);

async function retry() {
    retrying.value = true;
    try {
        await execute();
    } finally {
        retrying.value = false;
    }
}
</script>
